import type { DispatchKind, IDispatch, IDispatchCounters, IDispatchSender } from '@/types/Dispatch'

/**
 * Percentual com o guard de denominador — o primitivo GENÉRICO de "contador sobre
 * contador".
 *
 * **Quem escolhe o denominador é o chamador**, e é exatamente por isso que ela
 * existe separada de `funnelRate`: a tela de detalhe tem DOIS denominadores — o
 * funil do LOTE divide por `counters.contacts`, o funil de um NÓ divide por `sent`
 * do próprio nó (`IDispatchNodeCounters` não tem `contacts`). Uma função que
 * embutisse um dos dois não serviria ao outro, e a segunda implementação é como
 * duas telas passam a discordar do mesmo disparo.
 *
 * NÃO é a única divisão de contador por contador da base: `TaxaCelula.tsx:35`
 * (lista de disparos) e `[dispatchId]/kanban/KanbanColumn.tsx:27` (fatia da coluna
 * sobre o quadro inteiro) repetem o guard inline. São duas cópias conhecidas e
 * deliberadamente não migradas aqui — quem for mexer nelas migra pra esta função em
 * vez de escrever a terceira. `Kpis.tsx` deixou de ser uma delas em `fe612b32`: os
 * quatro cartões chamam `funnelRate`/`taxaSobre` direto. `ReplyTiming.tsx:103`
 * divide pela MAIOR barra, não por um total — altura de histograma, não taxa.
 *
 * O que tem dono único aqui NÃO é a divisão, é o guard: `contacts: 0` (lote cujos
 * telefones foram todos descartados na validação) e `sent: 0` (nó que nunca mandou
 * mensagem) são estados NORMAIS, e `NaN` na tela é o sintoma de quem esqueceu.
 * Denominador `<= 0` — inclusive negativo, de doc adulterado — devolve 0.
 *
 * **Não clampa em 100** e é de propósito: os webhooks são independentes e
 * confirmação de leitura desligada no aparelho faz `replied > read` sem nenhum
 * evento perdido. Quem satura é a BARRA; o número marca "≥" e continua verdadeiro.
 */
export function taxaSobre(valor: number, denominador: number): number {
  return denominador > 0 ? (valor / denominador) * 100 : 0
}

/**
 * Taxa de um campo de `counters` sobre o LOTE. Vale inclusive em disparo
 * reconstruído: o backfill Fase B preenche sent/delivered/read/failed com
 * estatística real do cluster.
 *
 * O denominador de todo percentual do funil DO LOTE é `counters.contacts` — o lote
 * **PEDIDO**, escrito uma vez no insert (`model.py::DispatchCounters.contacts`).
 * Ele parou de encolher: contato que não entrou na fila vira `counters.skipped`,
 * nunca um `$set` em `contacts`. Não é `delivered`: `sent`/`delivered`/`read`/
 * `failed` são incrementados por webhooks independentes por um listener que
 * engole exceção (handoff §6), então um evento perdido faz o numerador passar o
 * denominador e o percentual estourar 100%.
 *
 * `skipped` e `no_status` entram aqui como qualquer outro campo — e por isso
 * "pulado" aparece como contador e coluna PRÓPRIOS, nunca escondido dentro do
 * denominador de outra taxa.
 *
 * O funil POR NÓ não passa por aqui: ele chama `taxaSobre(valor, node.sent)`,
 * outro denominador, outra pergunta.
 */
export function funnelRate(
  counters: IDispatchCounters,
  field: Exclude<keyof IDispatchCounters, 'contacts'>,
): number {
  return taxaSobre(counters[field], counters.contacts)
}

export function readRate(d: Pick<IDispatch, 'counters'>): number {
  return funnelRate(d.counters, 'read')
}

/**
 * Taxa de resposta sobre contatos.
 *
 * **A supressão em `reconstruido: true` foi REVOGADA** (back `f62638bf`). O
 * handoff de 05/08 mandava suprimir porque a Fase B do backfill gravava
 * `replied: 0` fixo; isso deixou de valer no MESMO dia (`ec34d82b`), quando a
 * Fase B passou a atribuir o reply histórico pela janela do template — e o
 * documento não foi atualizado junto. O front implementou corretamente um doc
 * que ficou falso, e o sintoma era o KPI em "—" enquanto o donut de tipos de
 * resposta mostrava o dado, os dois saindo do MESMO documento.
 *
 * `contacts === 0` (lote cujos telefones foram todos descartados na validação)
 * devolve `0` — ver `funnelRate`.
 */
export function replyRate(d: Pick<IDispatch, 'counters'>): number {
  return funnelRate(d.counters, 'replied')
}

/** Taxa de falha sobre contatos. Mesmo denominador das outras — comparável na horizontal. */
export function failRate(d: Pick<IDispatch, 'counters'>): number {
  return funnelRate(d.counters, 'failed')
}

/** Taxa de entrega sobre contatos. */
export function deliveryRate(d: Pick<IDispatch, 'counters'>): number {
  return funnelRate(d.counters, 'delivered')
}

/**
 * Prefixo das chaves de resposta interativa, em `response_breakdown` (donut) e em
 * `reply_kind` (card do kanban). Vive aqui, e não em cada tela, porque as duas
 * superfícies TÊM que concordar: divergir é o defeito que fazia donut e kanban
 * contarem o mesmo disparo de dois jeitos.
 *
 * Fonte: `app/domains/company/whatsapp/dispatches/domain.py::reply_breakdown_key`
 * — `button:<rótulo>` cobre quick-reply de template, botão interativo e item de
 * lista, todos normalizados num prefixo só.
 */
export const REPLY_BUTTON_PREFIX = 'button:'

/**
 * Rótulo exibível de uma chave de resposta; `null` quando a chave não é de botão.
 *
 * Três armadilhas do lado do back, todas medidas em `reply_breakdown_key`:
 * - **`button` puro NÃO é resposta de botão** — é o TIPO CRU, devolvido quando o
 *   rótulo falta. A chave nunca é `button:` com sufixo vazio, então testar o
 *   prefixo sem exigir conteúdo classificaria mal.
 * - o rótulo é texto livre do template e **pode conter `:`** — só o primeiro
 *   prefixo sai.
 * - o back **não faz `strip()`**: rótulo só de whitespace é truthy lá e produz
 *   `'button:  '`. A chave continua válida pra agrupar, mas não serve de título.
 *
 * O rótulo aqui já vem **sanitizado** (`.` e `$` viraram `_`) e **truncado em 64**
 * pelo back: `"R$ 50"` chega como `"R_ 50"`. É o preço de a chave ser segura como
 * caminho de `$inc` no Mongo, e o donut mostra exatamente o mesmo texto.
 */
export function replyButtonLabel(key: string): string | null {
  if (!key.startsWith(REPLY_BUTTON_PREFIX)) return null
  const label = key.slice(REPLY_BUTTON_PREFIX.length)
  return label.trim() ? label : null
}

/**
 * Tipos CRUS de `reply_breakdown_key` que têm tradução. A chave é o `type` da
 * mensagem recebida (`app/domains/company/whatsapp/dispatches/domain.py`), e o
 * back cai nela sempre que o rótulo do botão falta — por isso `button` e
 * `interactive` são entradas LEGÍTIMAS aqui, e não chaves de botão sem prefixo.
 *
 * Vive neste módulo, e não na tela, porque o funil do detalhe e o kanban leem o
 * MESMO espaço de chave: dois dicionários é como donut e kanban passaram a
 * contar o mesmo disparo de dois jeitos. Era `TYPE_LABELS`, privado do donut de
 * `[dispatchId]/page.tsx`, e morreu junto com ele.
 */
const REPLY_TYPE_LABELS: Record<string, string> = {
  text: 'Texto',
  image: 'Imagem',
  audio: 'Áudio',
  video: 'Vídeo',
  document: 'Documento',
  sticker: 'Sticker',
  reaction: 'Reação',
  location: 'Localização',
  /* "compartilhado" porque a unidade contada JÁ é contato: sem isso a fatia
     "Contato" leria como "os contatos", não como quem respondeu mandando um
     cartão. Mesmo título de `REPLY_KINDS_COM_COLUNA` no kanban, que o importa
     daqui. */
  contacts: 'Contato compartilhado',
  button: 'Botão',
  interactive: 'Interativo',
  /* A Meta manda `type: "unsupported"` pra mensagem que ela não entrega pela API (enquete,
     mensagem efêmera, tipo novo); o back conta como resposta com essa chave. */
  unsupported: 'Tipo não suportado',
}

/**
 * Rótulo exibível de QUALQUER chave de `response_breakdown` — botão ou tipo cru.
 *
 * Cascata: rótulo de botão → tipo traduzido → **chave crua**. O último degrau é
 * deliberado: chave que esta versão não conhece (`nfm_reply`, do épico de
 * WhatsApp Flows) aparece feia, mas aparece. Fatia com nome feio é recuperável;
 * fatia invisível some com a resposta de alguém.
 *
 * `button:   ` (rótulo só de whitespace, que o back não faz `strip`) não é
 * título utilizável e cai no tipo `button`.
 */
export function replyKeyLabel(key: string): string {
  const rotuloDeBotao = replyButtonLabel(key)
  if (rotuloDeBotao) return rotuloDeBotao
  const tipo = key.startsWith(REPLY_BUTTON_PREFIX) ? 'button' : key
  return REPLY_TYPE_LABELS[tipo] ?? key
}

/** Nome de exibição do remetente; `null` quando o disparo não tem sender (reconstruído). */
export function resolveSenderName(
  sender: IDispatchSender | null,
  systemUsers: { db_id: string; name: string }[],
): string | null {
  if (!sender) return null
  return systemUsers.find((u) => u.db_id === sender.user_id)?.name || sender.username
}

/**
 * Segmento da rota da tela. Estes helpers são o ÚNICO construtor desse path — nenhuma URL
 * de disparo é montada à mão no front. Manter assim: foi o que permitiu renomear o
 * diretório da rota (Task 12) num ponto só, e depois MOVER a árvore inteira de
 * `/chatbot/[channelId]/dispatches` para `/chatbot/dispatches` sem commit intermediário
 * com link 404 e sem redirect da rota antiga — as duas specs decidiram não ter um.
 */
const DISPATCHES_SEGMENT = 'dispatches'

/**
 * A lista é do TENANT, não do canal — a rota vive em `/chatbot/dispatches`, como
 * Contatos e Vagas. Antes ela morava sob `[channelId]` e prometia um recorte que
 * NÃO fazia: pedia a lista sem `channel_id` e filtrava só template/remetente/data
 * no cliente.
 *
 * Com `channelId`, o canal vira **filtro de tela** (`?channel_id=<id>`): quem
 * entra pelo canal abre com ele marcado e desmarca como qualquer outro filtro;
 * quem entra pela home abre sem recorte de canal.
 */
export function dispatchesPath(channelId?: string): string {
  const base = `/chatbot/${DISPATCHES_SEGMENT}`
  return channelId ? `${base}?channel_id=${encodeURIComponent(channelId)}` : base
}

export function dispatchDetailPath(dispatchId: string): string {
  return `/chatbot/${DISPATCHES_SEGMENT}/${dispatchId}`
}

/**
 * O id do painel do kanban dentro do detalhe (`?secao=`). Vive aqui porque é o que
 * `dispatchProtocolsPath` monta e o que `_lib/abasDoDisparo` declara — um literal
 * em dois lugares diverge no dia em que a seção for renomeada, e o sintoma seria um
 * link que cai silenciosamente no primeiro painel.
 */
export const PAINEL_KANBAN = 'kanban'

/**
 * O kanban de um disparo. **Deixou de ser rota própria**: virou o painel `?secao=kanban`
 * do detalhe, e `/messages` sobrevive só como redirect por causa de link já colado —
 * nenhum call site de `src/` aponta mais pra lá, inclusive o toast de pulados de
 * `new-conversation`, que chama esta função.
 *
 * `nodeId` recorta por subdisparo. Vazio é ausência, não filtro — o back tem
 * `min_length=1` e `?node_id=` daria 422.
 */
export function dispatchProtocolsPath(dispatchId: string, nodeId?: string): string {
  const sp = new URLSearchParams({ secao: PAINEL_KANBAN })
  if (nodeId) sp.set('node_id', nodeId)
  return `${dispatchDetailPath(dispatchId)}?${sp.toString()}`
}

/**
 * Rótulo da ORIGEM do disparo (`IDispatch.kind`). O valor do wire (`manual`,
 * `flow_outbound`) nunca aparece cru quando há tradução.
 *
 * Vive aqui, e não na lista, porque duas superfícies leem o mesmo campo: o filtro
 * de `/chatbot/dispatches` e o painel Detalhes do envio do detalhe. Era privado de
 * `dispatches/page.tsx` — mesma correção que `replyKeyLabel` recebeu no bloco 04.
 */
export const ROTULO_KIND: Record<DispatchKind, string> = {
  manual: 'Manual',
  transmission_group: 'Grupo de transmissão',
  flow_outbound: 'Flow',
}

/** Rótulo de um `kind` cru. Kind que esta versão não conhece volta cru — a mesma
 *  cascata de `replyKeyLabel`: nome feio é recuperável, linha invisível não. */
export function kindLabel(kind: string): string {
  return ROTULO_KIND[kind as DispatchKind] ?? kind
}
